import React, { useRef, useState } from 'react';
import { useMfa } from '../hooks/useMfa';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/Button';

const MAX_ATTEMPTS = 5;

const MfaChallenge: React.FC = () => {
  const { challengeAndVerify, loading } = useMfa();
  const { logout, refreshMfaGate, login } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const attempts = useRef(0);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    attempts.current += 1;

    if (attempts.current > MAX_ATTEMPTS) {
      await logout();
      return;
    }

    setError('');
    try {
      await challengeAndVerify(code);
      await refreshMfaGate();
      login();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Código inválido';
      setError(msg);
      setCode('');
      if (attempts.current >= MAX_ATTEMPTS) {
        setError('Muitas tentativas. Redirecionando...');
        setTimeout(() => void logout(), 2000);
      }
    }
  };

  const handleCodeChange = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6) setTimeout(() => void handleVerify(), 50);
  };

  const remaining = MAX_ATTEMPTS - attempts.current;

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted p-6">
      <article className="w-full max-w-sm rounded-2xl bg-background p-8 shadow-lg ring-1 ring-border">
        <header className="mb-6 text-center">
          <h1 className="text-xl font-bold text-foreground">Verificação em 2 etapas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Digite o código do Google Authenticator
          </p>
        </header>

        <input
          id="totp-challenge"
          type="text"
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          placeholder="000000"
          className="w-full min-h-16 rounded-lg border border-border bg-background text-center font-mono text-4xl tracking-[0.6em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />

        {error && (
          <p role="alert" className="mt-3 text-center text-sm text-destructive">{error}</p>
        )}
        {attempts.current > 0 && remaining > 0 && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Tentativas restantes: {remaining}
          </p>
        )}

        <Button
          type="button"
          className="mt-5 w-full"
          disabled={loading || code.length !== 6}
          onClick={() => void handleVerify()}
        >
          {loading ? 'Verificando...' : 'Verificar'}
        </Button>

        <button
          type="button"
          onClick={() => void logout()}
          className="mt-3 w-full text-sm text-muted-foreground hover:text-foreground"
        >
          Usar outra conta
        </button>
      </article>
    </main>
  );
};

export default MfaChallenge;
